import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
    BugIcon,
    ChatTeardropDotsIcon,
    LightbulbIcon,
    LifebuoyIcon,
    CheckCircleIcon,
    PaperPlaneTiltIcon,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";

const CATEGORIES = [
    {
        key: "bug",
        label: "Report a Bug",
        icon: BugIcon,
        desc: "Something broken? Tell us what went wrong and where.",
        color: "#E01A4F",
    },
    {
        key: "feedback",
        label: "Feedback",
        icon: ChatTeardropDotsIcon,
        desc: "Share what's working, what's not. We read every word.",
        color: "#FFA700",
    },
    {
        key: "feature",
        label: "Feature Request",
        icon: LightbulbIcon,
        desc: "Have an idea? We ship requests from creators every week.",
        color: "#E01A4F",
    },
    {
        key: "support",
        label: "Contact Support",
        icon: LifebuoyIcon,
        desc: "Need help with account, payments, or something else?",
        color: "#FFA700",
    },
];

export default function Support() {
    const [category, setCategory] = useState("bug");
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
        device: "",
        app_version: "",
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [err, setErr] = useState("");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await api.post("/support/tickets", { ...form, category });
            setSuccess(true);
            setForm({ name: "", email: "", subject: "", message: "", device: "", app_version: "" });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setLoading(false);
        }
    };

    const active = CATEGORIES.find((c) => c.key === category);
    const needsDevice = category === "bug";

    return (
        <div className="px-5 md:px-10 max-w-5xl mx-auto py-16 md:py-24" data-testid="support-page">
            <Seo title="Support — India Reels" description="Submit a bug report, feedback, feature request or contact India Reels support." path="/support" />
            <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                support center
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl leading-tight">
                We&apos;re here to <span className="gradient-text">help</span>.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-2xl">
                Pick a category below — we route every submission to the right team. Average reply
                time: 12 hours.
            </p>

            {/* Category cards */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                {CATEGORIES.map((c) => {
                    const isActive = category === c.key;
                    return (
                        <button
                            key={c.key}
                            data-testid={`category-${c.key}`}
                            onClick={() => setCategory(c.key)}
                            className={`text-left rounded-3xl border p-5 transition-all ${
                                isActive
                                    ? "border-primary/60 bg-primary/10 shadow-[0_0_30px_rgba(224,26,79,0.2)]"
                                    : "border-white/10 bg-surface hover:border-white/20"
                            }`}
                        >
                            <div
                                className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3"
                                style={{ background: `${c.color}1F` }}
                            >
                                <c.icon size={22} weight="fill" color={c.color} />
                            </div>
                            <div className="font-heading font-bold text-sm md:text-base">
                                {c.label}
                            </div>
                            <div className="text-text-secondary text-xs mt-1 leading-snug hidden md:block">
                                {c.desc}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Form */}
            <div className="mt-8 rounded-3xl border border-white/10 bg-surface p-6 md:p-10">
                {success ? (
                    <div className="text-center py-12 fade-up" data-testid="support-success">
                        <div className="w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-5">
                            <CheckCircleIcon size={46} weight="fill" color="#E01A4F" />
                        </div>
                        <h2 className="font-heading font-black text-2xl md:text-3xl">
                            Ticket submitted! 🎉
                        </h2>
                        <p className="text-text-secondary mt-3 max-w-md mx-auto">
                            Our team has received your {active.label.toLowerCase()}. Average response
                            time is 12 hours.
                        </p>
                        <button
                            onClick={() => setSuccess(false)}
                            data-testid="support-send-another"
                            className="btn-ghost mt-6 px-5 py-2.5 text-sm"
                        >
                            Submit another
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="flex items-center gap-3 pb-2">
                            <div
                                className="w-10 h-10 rounded-2xl flex items-center justify-center"
                                style={{ background: `${active.color}1F` }}
                            >
                                <active.icon size={20} weight="fill" color={active.color} />
                            </div>
                            <h2 className="font-heading font-bold text-xl">{active.label}</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <Field
                                label="Your name"
                                testid="support-name"
                                value={form.name}
                                onChange={set("name")}
                                placeholder="Aapka naam"
                                required
                            />
                            <Field
                                label="Email"
                                testid="support-email"
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                placeholder="aap@example.com"
                                required
                            />
                        </div>

                        <Field
                            label="Subject"
                            testid="support-subject"
                            value={form.subject}
                            onChange={set("subject")}
                            placeholder={`Short summary of your ${active.label.toLowerCase()}`}
                            required
                        />

                        {needsDevice && (
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field
                                    label="Device (optional)"
                                    testid="support-device"
                                    value={form.device}
                                    onChange={set("device")}
                                    placeholder="e.g. Redmi Note 12, iPhone 14"
                                />
                                <Field
                                    label="App version (optional)"
                                    testid="support-version"
                                    value={form.app_version}
                                    onChange={set("app_version")}
                                    placeholder="e.g. 1.2.3"
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                                {category === "bug" ? "Describe the bug" : "Your message"}
                            </label>
                            <textarea
                                data-testid="support-message"
                                value={form.message}
                                onChange={set("message")}
                                rows={6}
                                required
                                minLength={5}
                                maxLength={4000}
                                placeholder={
                                    category === "bug"
                                        ? "Steps to reproduce, what you expected, what happened..."
                                        : "Tell us in detail..."
                                }
                                className="input-field mt-2 resize-none"
                            />
                            <div className="text-right text-[11px] text-text-secondary mt-1">
                                {form.message.length}/4000
                            </div>
                        </div>

                        {err && (
                            <div
                                data-testid="support-error"
                                className="text-primary text-sm bg-primary/10 px-4 py-3 rounded-xl"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="support-submit"
                            className="btn-primary w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            <PaperPlaneTiltIcon size={18} weight="fill" />
                            {loading ? "Submitting..." : `Submit ${active.label.toLowerCase()}`}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ label, testid, ...props }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                {label}
            </label>
            <input data-testid={testid} className="input-field mt-2" {...props} />
        </div>
    );
}
